import React, { useMemo } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider, CssBaseline } from "@mui/material";
import type { Theme } from "@mui/material/styles";

import { App } from "./App";
import { buildTheme, isThemeId, resolveModule, type ThemeId } from "./theme";
import { useResolvedColorMode } from "@/store/colorMode";
import { useDensity } from "@/store/density";
import { useEffectiveThemeId } from "@/store/theme";
import { GlobalToast } from "@/components/GlobalToast";
import { documentTitle } from "@/shared/brand";

document.title = documentTitle;

// Registered in production builds only. In development a service worker would sit between Vite's
// module server and the page and make hot reload unpredictable. It caches nothing — see public/sw.js.
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 4xx is an answer, not a failure to reach the server — retrying a 403 three times just
      // delays the message the user needs to see.
      retry: (failureCount, error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

/** `?theme=teal` forces a palette for this page load only — a review hatch, never persisted. */
const QUERY_THEME: ThemeId | null = (() => {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("theme");
  return isThemeId(raw) ? raw : null;
})();

/**
 * Built themes, keyed by every input that affects them.
 *
 * <p>The departmental theme swaps its accent on navigation, so without this the app would rebuild a
 * theme object on every route change. The key set is small and bounded — modules × two modes × two
 * densities — so it is built once per session and reused.</p>
 */
const themeCache = new Map<string, Theme>();

function ThemedApp() {
  // Resolved rather than the raw preference: "system" is already light or dark by this point.
  const mode = useResolvedColorMode();
  const density = useDensity((s) => s.density);
  const effective = useEffectiveThemeId();
  const themeId = QUERY_THEME ?? effective;

  // Only the departmental theme cares where you are; the rest ignore the module entirely.
  const { pathname } = useLocation();
  const moduleKey = themeId === "departmental" ? resolveModule(pathname) : null;

  const cacheKey = `${themeId}:${mode}:${density}:${moduleKey ?? "-"}`;
  const theme = useMemo(() => {
    let built = themeCache.get(cacheKey);
    if (!built) {
      built = buildTheme(themeId, mode, density, moduleKey);
      themeCache.set(cacheKey, built);
    }
    return built;
    // cacheKey encodes every input above; listing them again would only risk them drifting apart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <App />
      <GlobalToast />
    </ThemeProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemedApp />
      </QueryClientProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
